import React, { memo } from 'react';
import { FaChevronDown } from 'react-icons/fa';

interface MoreBelowProps {
  /** Tiles still below the visible part of the scroll area; nothing renders at 0 */
  count: number;
  /** Scroll the area one row down */
  onShowMore: () => void;
}

/**
 * The bottom fade and the "N more below" chip of a vertically scrolling tile
 * area (issue #123). Most mice only scroll vertically, so the chip is a real
 * button that scrolls one row, not a decoration.
 *
 * Positioned against the nearest positioned ancestor, which must be the box
 * around the scroll area (not the scrolled content), so both stay put while
 * the tiles move under them.
 */
const MoreBelowComponent: React.FC<MoreBelowProps> = ({ count, onShowMore }) => {
  if (count <= 0) return null;

  return (
    <>
      <div
        aria-hidden="true"
        data-testid="more-below-fade"
        className="pointer-events-none absolute inset-x-0 bottom-0 h-28 bg-gradient-to-b from-transparent to-game-navy"
      />
      <button
        type="button"
        onClick={onShowMore}
        className="absolute bottom-3 left-1/2 inline-flex h-9 -translate-x-1/2 items-center gap-2 whitespace-nowrap rounded-full border border-game-medium bg-[var(--lk-bg2)] px-4 text-sm font-semibold text-game-cream shadow-lg transition-colors duration-200 hover:bg-[var(--lk-bg3)] hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-game-golden focus-visible:ring-offset-2 focus-visible:ring-offset-game-navy"
      >
        <span>{count} more below</span>
        <FaChevronDown aria-hidden="true" className="text-xs" />
      </button>
    </>
  );
};

export const MoreBelow = memo(MoreBelowComponent);
MoreBelow.displayName = 'MoreBelow';
